import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import SingleColumnTemplate from '../../../ui/layouts/SingleColumnTemplate.jsx';
import SprintListTable from '../components/Sprints/SprintListTable';
import SprintFilters from '../components/Sprints/SprintFilters';
import SprintStatsCards from '../components/Sprints/SprintStatsCards';
import Button from '../../../ui/canon/Button.jsx';
import { PlusIcon } from '@heroicons/react/24/outline';

const SPRINTS_QUERY = gql`
  query Sprints($status: [SprintStatusEnum!]) {
    sprints(status: $status) {
      id
      name
      objectiveText
      startOn
      endOn
      status
      source_type
      source_id
      metrics_snapshot
      enrollment_id
      overdue_count
      blocked_count
      coachProfile {
        id
        name
        avatar_url
      }
    }
  }
`;

const CLOSE_SPRINT = gql`
  mutation UpdateSprint($id: ID!, $input: UpdateSprintInput!) {
    updateSprint(id: $id, input: $input) {
      id
      status
      metricsSnapshot
    }
  }
`;

const SprintsListContainer = () => {
  const [filters, setFilters] = useState({});
  const { data, loading, error, refetch } = useQuery(SPRINTS_QUERY, {
    variables: { status: null },
  });
  const [updateSprint] = useMutation(CLOSE_SPRINT);

  // Calculate sprint counts for filters
  const allSprints = data?.sprints || [];
  const sprintCounts = useMemo(() => {
    const counts = {
      total: allSprints.length,
      planned: 0,
      active: 0,
      closed: 0,
      abandoned: 0,
      diy: 0,
      coach_led: 0,
    };

    allSprints.forEach((sprint) => {
      const status = sprint.status?.toUpperCase();
      if (status === 'PLANNED') counts.planned++;
      else if (status === 'ACTIVE') counts.active++;
      else if (status === 'CLOSED') counts.closed++;
      else if (status === 'ABANDONED') counts.abandoned++;

      if (sprint.source_type === 'SprintProgram') {
        counts.coach_led++;
      } else {
        counts.diy++;
      }
    });

    return counts;
  }, [allSprints]);

  // Apply filters
  const filteredSprints = useMemo(() => {
    let filtered = [...allSprints];

    // Status filter
    if (filters.status) {
      filtered = filtered.filter((sprint) => sprint.status?.toUpperCase() === filters.status.toUpperCase());
    }

    // Sprint type filter
    if (filters.sprintType) {
      if (filters.sprintType === 'coach_led') {
        filtered = filtered.filter((sprint) => sprint.source_type === 'SprintProgram');
      } else if (filters.sprintType === 'diy') {
        filtered = filtered.filter((sprint) => !sprint.source_type || sprint.source_type !== 'SprintProgram');
      }
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (sprint) =>
          sprint.name?.toLowerCase().includes(searchLower) ||
          sprint.objectiveText?.toLowerCase().includes(searchLower) ||
          sprint.coachProfile?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Date range filter
    if (filters.startDate || filters.endDate) {
      filtered = filtered.filter((sprint) => {
        if (!sprint.startOn && !sprint.endOn) return false;

        const sprintStart = sprint.startOn ? new Date(sprint.startOn) : null;
        const sprintEnd = sprint.endOn ? new Date(sprint.endOn) : null;

        if (filters.startDate) {
          const filterStart = new Date(filters.startDate);
          if (sprintEnd && sprintEnd < filterStart) return false;
        }

        if (filters.endDate) {
          const filterEnd = new Date(filters.endDate);
          if (sprintStart && sprintStart > filterEnd) return false;
        }

        return true;
      });
    }

    return filtered;
  }, [allSprints, filters]);

  // Normalize sprint data
  const normalizedSprints = useMemo(() => {
    if (!filteredSprints || filteredSprints.length === 0) {
      return [];
    }
    return filteredSprints.map((s) => ({
      ...s,
      objectiveText: s.objectiveText || s.objective_text,
      startOn: s.startOn || s.start_on,
      endOn: s.endOn || s.end_on,
      sourceType: s.source_type || s.sourceType,
      sourceId: s.source_id || s.sourceId,
      metricsSnapshot: s.metrics_snapshot || s.metricsSnapshot || {
        completionPct: 0,
        totalItems: 0,
        completedItems: 0,
      },
      enrollmentId: s.enrollment_id || s.enrollmentId,
      overdueCount: s.overdue_count || s.overdueCount || 0,
      blockedCount: s.blocked_count || s.blockedCount || 0,
      coachProfile: s.coachProfile || s.coach_profile,
    }));
  }, [filteredSprints]);

  const handleClose = async (sprintWithNotes) => {
    try {
      await updateSprint({
        variables: {
          id: sprintWithNotes.id,
          input: {
            status: 'CLOSED',
            retrospectiveNotes: sprintWithNotes.retrospectiveNotes,
          },
        },
      });
      refetch();
    } catch (err) {
      console.error('Error closing sprint:', err);
      alert('Failed to close sprint. Please try again.');
    }
  };

  const handleOpen = (sprint) => {
    if (sprint.sourceType === 'SprintProgram' && sprint.enrollmentId) {
      window.location.href = `/sprints/enrollments/${sprint.enrollmentId}`;
    } else {
      window.location.href = `/succession/sprint?sprintId=${sprint.id}`;
    }
  };

  const handleEdit = (sprint) => {
    const event = new CustomEvent('open-sprint-edit', { detail: sprint });
    window.dispatchEvent(event);
  };

  if (loading) {
    return (
      <SingleColumnTemplate>
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6 text-succession-text-primary">
          Loading...
        </div>
      </SingleColumnTemplate>
    );
  }

  if (error) {
    return (
      <SingleColumnTemplate>
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6 text-succession-text-primary">
          Error loading sprints: {error.message}
        </div>
      </SingleColumnTemplate>
    );
  }

  return (
    <SingleColumnTemplate variant="wide">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-succession-text-primary">My Sprints</h1>
            <p className="text-sm text-succession-text-secondary mt-1">
              Manage and track your sprint progress
            </p>
          </div>
          <Button variant="primary" size="md" onClick={() => {
            const event = new CustomEvent('open-sprint-create');
            window.dispatchEvent(event);
          }}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Create Sprint
          </Button>
        </div>

        {/* Stats Cards */}
        <SprintStatsCards sprintCounts={sprintCounts} />

        {/* Filters */}
        <SprintFilters
          filters={filters}
          onFiltersChange={setFilters}
          sprintCounts={sprintCounts}
        />

        {/* Table */}
        <SprintListTable
          sprints={normalizedSprints}
          onClose={handleClose}
          onEdit={handleEdit}
          onOpen={handleOpen}
        />
      </div>
    </SingleColumnTemplate>
  );
};

export default SprintsListContainer;
